// src/pages/GenerateInvoice.js
// This component handles invoice creation, tax preview, and PDF generation.
// It uses the shared Axios instance with automatic JWT injection for protected calls.

import React, { useState, useEffect } from 'react';
import {
  Form,
  Input,
  Select,
  Button,
  Row,
  Col,
  message,
  DatePicker,
  InputNumber,
  Typography,
  Collapse,
  Table,
  Radio,
} from 'antd';
import { useForm, useWatch } from 'antd/lib/form/Form';
import { DownCircleOutlined, MinusCircleOutlined, UpCircleOutlined } from '@ant-design/icons';
// import { Button } from '../components/ui/button';
import { getActiveCustomers } from '../customer/service';
import { getCustomerSubscriptions } from '../subscription/service';
import dayjs from 'dayjs';
import { getProducts } from '../product/service';
import useInvoiceState from './hooks/useInvoiceState';
import { formatPrice } from '../../utils/common';
import TextArea from 'antd/lib/input/TextArea';

function GenerateInvoice() {
  const [messageApi, contextHolder] = message.useMessage();
  const { addOrUpdate, downloadPDFFile, commitTransacitionData, voidTransacitionData } = useInvoiceState();
  const [form] = useForm();
  const formValues = useWatch([], form);
  const { Option } = Select;
  const [customers, setCustomers] = useState([]);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [customer, setCustomer] = useState({});
  const [subscriptions, setSubscriptions] = useState([]);
  const [customersList, setCustomersList] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState();
  const [productsList, setProductsList] = useState([]);
  const [bundledProducts, setBundledProducts] = useState([]);
  const [isBundled, setIsBundled] = useState(false);
  const [isCommitted, setIsCommitted] = useState(0);

  useEffect(() => {
    setLoadingCustomers(true);
    getActiveCustomers().then(data => {
      setCustomers(data);
    }).catch(err => {
      messageApi.error('Failed to fetch customers', 5);
    }).finally(() => {
      setLoadingCustomers(false);
    });
    getProducts().then(data => {
      setProductsList(data);
    });
    form.setFieldsValue({'total_discount': "0"});
  }, []);

  useEffect(() => {
    form.setFieldValue('subscription_items', subscriptions)
  }, [subscriptions])

  const getCustomerSubscriptionsData = (id) => {
    setLoading(true);
    setIsBundled(false);
    setBundledProducts([]);
    getCustomerSubscriptions(id).then(data1 => {
      const data = data1 || {};

      const productsData = [];
      (data.subscription_items || []).map((products) => {
        if (products.is_active) {
          productsData.push(products);
        } else {
          const deactivationEffectiveDate = dayjs(products.last_deactivation_date).valueOf();
          if (dayjs().valueOf() < deactivationEffectiveDate) {
            // Only add future deactivation products to the invoice list items
            productsData.push(products);
          }
        }
      })
      data.subscription_items = productsData;

      (data.subscription_items || []).sort((a, b) => {
        return Number(a.product.is_credit_memo) - Number(b.product.is_credit_memo);
      })
      setLoading(false);
      let totalDiscount = 0;
      let subTotal = 0;
      const dateFormattedSubscriptions = (data.subscription_items || []).map(item => {
        if (item.product.is_bundled) {
          setIsBundled(true);
          const bundleProductsList = [];
          item.product.bundles.map((bundledProductsList) => {
            bundleProductsList.push(bundledProductsList.bundle_item);
          });
          setBundledProducts([...bundledProducts, ...bundleProductsList]);
        }
        const unit = parseFloat(item.unit_price || 0);
        const percent = parseFloat(item.discount_percentage || 0);
        const fixed = parseFloat(item.discount_amount || 0);
        const quantity = (+item.quantity || 1);
        const isCreditMemo = item.product.is_credit_memo;

        let discountAmount = fixed;
        const multiplier = isCreditMemo ? -1 : 1;
        subTotal += (unit * quantity * multiplier);

        if (unit > 0) {
          discountAmount = (unit * percent) / 100;
        }
        totalDiscount += discountAmount;

        const amount = (unit * quantity) - discountAmount;

        return {
          ...item,
          invoice_description: item.product.name,
          unit_price: unit,
          price_unit: item.price_unit || '',
          discount_percentage: percent,
          discount_amount: fixed,
          before_discount: (unit * quantity),
          amount: amount,
          quantity,
          quantity_unit: item.quantity_unit || '',
          is_bundled: item.product.is_bundled,
          is_taxable: item.product.is_taxable,
          is_credit_memo: isCreditMemo,
          term: item.term,
          start_date: dayjs(item.start_date, 'YYYY-MM-DD'),
          end_date: dayjs(item.end_date, 'YYYY-MM-DD'),
        }
      });
      // form.setFieldsValue({ 'total_discount': totalDiscount.toFixed(2) });
      // form.setFieldsValue({ 'sub_total': subTotal.toFixed(2) });
      // form.setFieldsValue({ 'total': (subTotal - totalDiscount).toFixed(2) })
      // form.setFieldsValue({ 'credits': "0.00" });
      form.setFieldsValue({
        for: '',
        duration: '',
        credits: '0.00',
        total: (subTotal - totalDiscount).toFixed(2),
        sub_total: subTotal.toFixed(2),
        total_discount: totalDiscount.toFixed(2),
        invoice_date: dayjs(),
        invoice_due_date: dayjs(),
        tax: '0.00',
      });
      if (data.subscription_items.length > 0) {
        const days = data.subscription_items[0]?.invoice_due_days || 0;
        if (days) {
          form.setFieldValue('invoice_due_date', dayjs().add(days, 'days'));
        }
      }
      setSubscriptions(dateFormattedSubscriptions);
      if (dateFormattedSubscriptions.length > 0) {
        setCustomer(data.customer);
        setSelectedCustomer(data.customer.id);
        setCustomersList([data.customer]);
      } else {
        setCustomer({});
        setSelectedCustomer(id);
      }
    });
  }

  const handleValueChanges = (_, subscriptionsData) => {
    if (_.subscription_items) {
      _.subscription_items.forEach((item, index) => {
        if (item && subscriptionsData.subscription_items[index]) {
          let quantityUnit = subscriptionsData.subscription_items[index].price_unit;
          const quantity = subscriptionsData.subscription_items[index].quantity;
          if (quantity > 1) {
            quantityUnit = `${quantityUnit}s`;
          }
          subscriptionsData.subscription_items[index].quantity_unit = quantityUnit;
        }
      });

    }
    let totalDiscount = 0;
    let subTotal = 0;
    const updated = subscriptionsData.subscription_items?.map((item = {}) => {
      const unit = parseFloat(item.unit_price || 0);
      const percent = parseFloat(item.discount_percentage || 0);
      const fixed = parseFloat(item.discount_amount || 0);
      const quantity = +item.quantity || 1;
      const isCreditMemo = item.product.is_credit_memo;
      const multiplier = isCreditMemo ? -1 : 1;

      let discountAmount = fixed;
      let discountPercentage = percent;
      subTotal += (unit * quantity * multiplier);

      if (unit > 0) {
        discountAmount = (unit * percent) / 100;
      }
      totalDiscount += discountAmount;

      const amount = (unit * quantity) - discountAmount;

      return {
        ...item,
        is_bundled: item.product.is_bundled,
        before_discount: unit,
        discount_amount: discountAmount.toFixed(2),
        discount_percentage: discountPercentage.toFixed(2),
        amount: amount.toFixed(2),
      }
    });

    // if (subscriptionsData.subscription_items.length > 0) {
    //   const days = subscriptionsData.subscription_items[0]?.invoice_due_days || 0;
    //   if (days) {
    //     const invoiceDate = form.getFieldValue('invoice_date');
    //     form.setFieldValue('invoice_due_date', dayjs(invoiceDate).add(days, 'days'));
    //   }
    // }
    let credits = form.getFieldValue('credits');
    let tax = +form.getFieldValue('tax');
    form.setFieldValue( 'subscription_items', updated )
    form.setFieldsValue({ 'total_discount': totalDiscount.toFixed(2) });
    form.setFieldsValue({ 'sub_total': subTotal.toFixed(2) });
    form.setFieldsValue({ 'total': (subTotal - totalDiscount - +credits + tax).toFixed(2) })
  };

  const calculateTax = async (values) => {
    const customerData = customers.find(c => +c.id === +values.customer_id);
    if (!customerData) {
      messageApi.error('Please select a customer', 5);
      return false;
    }
    if (values.subscription_items.length === 0) {
      messageApi.error('No products to generate invoice.', 5);
      return false;
    }
    values.invoice_date = dayjs(values.invoice_date).format('YYYY-MM-DD');
    values.invoice_due_date = dayjs(values.invoice_due_date).format('YYYY-MM-DD');
    values.subscription_items = values.subscription_items.map((item) => {
      return {
        ...item,
        start_date: dayjs(item.start_date).format('YYYY-MM-DD'),
        end_date: dayjs(item.end_date).format('YYYY-MM-DD'),
      };
    });

    try {
      setLoading(true);
      const res = await addOrUpdate(values);
      setLoading(false);
      if (res.success) {
        // console.log({ responseData: res });
        // console.log({preview: { customer: customerData, ...res.data.data }});
        const tax = +res.data.data.totalTax;
        form.setFieldValue('tax', tax.toFixed(2));
        const total = +form.getFieldValue('total');
        form.setFieldValue('total', (tax+total).toFixed(2));
        setPreview({ customer: customerData, ...res.data.data });
        setIsCommitted(res.data.data.isTaxed ? 0 : 1);
      } else {
        messageApi.error(res.message, 5);
      }
    } catch (err) {
      setLoading(false);
      setPreview(null);
      console.error('Preview failed:', err);
      messageApi.error('Preview error. See console.', 5);
    }
  };

  const submitInvoice = async (commit = 0) => {
    if (!preview) return alert('Please preview invoice first');
    try {
      setLoading(true);
      if (commit === 1) {
        const res = await commitTransacitionData({ invoice_id: preview.invoice_id });
        setLoading(false);
        if (res?.success) {
          setIsCommitted(commit);
          messageApi.success('Invoice Commit success', 5);
        } else {
          if (!res?.success) {
            messageApi.error(res.message, 5);
          }
        }
      } else if (commit === 2) {
        const res = await voidTransacitionData({ invoice_id: preview.invoice_id });
        setLoading(false);
        if (res?.success) {
          setIsCommitted(commit);
          messageApi.success('Invoice Void success', 5);
        } else {
          if (!res?.success) {
            messageApi.error(res.message, 5);
          }
        }
      } else {
        const res = await downloadPDFFile({ invoice_id: preview.invoice_id });
        setLoading(false);
        if (res.success) {
          const blob = new Blob([res.data], { type: 'application/pdf' });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = res.fileName;
          a.click();
          messageApi.success('Invoice downloaded', 5);
        } else {
          messageApi.error(res.message, 5);
        }
      }
    } catch (error) {
      setLoading(false);
      if (commit === 1) {
        messageApi.error('Invoice commit failed.', 5);
      } else if (commit === 2) {
        messageApi.error('Invoice void failed.', 5);
      } else {
        messageApi.error('Invoice download failed.', 5);
      }
      console.error('PDF download/commit failed:', commit, error);
    }
  };

  const selectCustomer = (value) => {
    form.setFieldsValue({'customer_id': value});
    getCustomerSubscriptionsData(value)
  }

  const formatDate = (date) => {
    if (date) {
      return dayjs(date, 'YYYY-MM-DD').format('MM/DD/YYYY');
    }
    return null;
  }

  const getProductById = (id) => {
    if (id) {
      const data = productsList.find(item => item.id === id);
      let skuData = '';
      if (data.sku) {
        skuData = ` (${data.sku})`;
      }
      return `${data.name}${skuData}`;
    }
    return null;
  }

  const tax_columns = [
    {
      title: 'Tax Name',
      dataIndex: 'taxName',
      key: 'taxName',
    },
    {
      title: 'Taxable Amount',
      dataIndex: 'taxable',
      key: 'taxable',
      render: (value) => formatPrice(value),
    },
    {
      title: 'Tax (%)',
      dataIndex: 'rate',
      key: 'rate',
      render: (value) => `${formatPrice(value * 100, false)}%`,
    },
    {
      title: 'Tax',
      dataIndex: 'tax',
      key: 'tax',
      render: (value) => formatPrice(value),
    }
  ];

  const bundle_rows = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Product',
      dataIndex: 'product',
      key: 'product',
    },
    {
      title: 'SKU',
      dataIndex: 'sku',
      key: 'sku',
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'License Type',
      dataIndex: 'license_type',
      key: 'license_type',
    },
    {
      title: 'Version',
      dataIndex: 'version',
      key: 'version',
    },
    {
      title: 'Original Price',
      dataIndex: 'price',
      key: 'price',
      render: (value) => formatPrice(value),
    }
  ];

  const invoice_types = [
    {
      value: 1,
      label: 'Regular',
      is_default: true,
    },
    {
      value: 2,
      label: 'T&M',
      is_default: false,
    },
  ];
  const defaultInvoiceType = invoice_types.find((item) => item.is_default === true);
  const [invoiceType, setInvoiceType] = useState(defaultInvoiceType.value || null);

  return (
    <>
      {contextHolder}
      {/* <div className="mb-4">
        <label>Select Customer:</label>
        <select onChange={(e) => setSelected(e.target.value)} className="border ml-2 p-2 rounded">
          <option value="">-- Select --</option>
          {customers.map(c => (
            <option key={c.code || c.name} value={c.code || c.name}>{c.name} ({c.code})</option>
          ))}
        </select>
      </div>

      {items.map((item, i) => (
        <div key={i} className="flex gap-2 mb-2">
          <input placeholder="Description" className="border p-2 rounded" value={item.description} onChange={(e) => handleItemChange(i, 'description', e.target.value)} />
          <input type="number" placeholder="Qty" className="border p-2 rounded w-20" value={item.quantity} onChange={(e) => handleItemChange(i, 'quantity', e.target.value)} />
          <input type="number" placeholder="Unit Price" className="border p-2 rounded w-24" value={item.unit_price} onChange={(e) => handleItemChange(i, 'unit_price', e.target.value)} />
        </div>
      ))} */}

      <Form
        form={form}
        disabled={preview}
        name="userForm"
        layout="vertical"
        onFinish={calculateTax}
        initialValues={{
          invoice_type: invoiceType,
          customer_id: '',
          subscription_items: [{}],
          total_discount: 0,
        }}
        onValuesChange={handleValueChanges}
        onKeyDown={(e) => {
          const tagName = e.target.tagName.toLowerCase();
          if (e.key === 'Enter' && tagName !== 'textarea') e.preventDefault();
        }}
      >
        <Row align="middle" className='mb-24'>
          <Col>
            <label className='mr-8'>Invoice Type:</label>
          </Col>
          <Col span={6}>
            <Form.Item
              name="invoice_type"
              noStyle
              rules={[{ required: true, message: 'Please select invoice type' }]}
            >
              <Radio.Group 
                options={invoice_types}
                onChange={(attr) => { setInvoiceType(attr.target.value) }}
                style={{ width: '250px'}}
                optionType='button'
                disabled={loadingCustomers || preview}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row align="middle" className='mb-24'>
          <Col>
            <label className='mr-8'>Select Customer:</label>
          </Col>
          <Col span={6}>
            <Form.Item
              name="customer_id"
              noStyle
              rules={[{ required: true, message: 'Please select a customer' }]}
            >
              <Select
                placeholder="Select a Customer"
                notFoundContent="No customer available"
                onChange={(value) => {selectCustomer(value)}}
                style={{ width: '250px'}}
                showSearch
                optionFilterProp='children'
                disabled={loadingCustomers || preview}
              >
                {customers?.length > 0 && customers.map((c) => (
                  <Option key={c.id} value={c.id}>{c.name} ({c.code})</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
        {selectedCustomer &&
          <Row align="middle" style={{ marginBottom: 24, display: 'flex' }}> 
            <Col style={{ flex: 1}}>
              <label style={{ marginRight: 8 }}>
                <strong>Address</strong>:
                {customer.address}, {customer.city}, {customer.state}, {customer.zip}, {customer.country}
              </label>
            </Col>
            <Col style={{ flex: 1}}>
              <label style={{ marginRight: 8 }}>
                <strong>Invoice Date</strong>:
                <Form.Item
                  name="invoice_date"
                  noStyle
                  rules={[{ required: true, message: 'Please select invoice date' }]}
                >
                  <DatePicker format={'MM/DD/YYYY'}/>
                </Form.Item>
              </label>
            </Col>
            <Col style={{ flex: 1}}>
              <label style={{ marginRight: 8 }}>
                <strong>Invoice Due Date</strong>:
                <Form.Item
                  name="invoice_due_date"
                  noStyle
                  rules={[{ required: true, message: 'Please select invoice due date' }]}
                >
                  <DatePicker format={'MM/DD/YYYY'}/>
                </Form.Item>
              </label>
            </Col>
          </Row>
        }
        { selectedCustomer &&
          <Row align="middle" style={{ marginBottom: 24, display: 'flex' }}>
            <Col style={{ flex: 1}}>
              <label style={{ marginRight: 8 }}>
                <strong>For</strong>:
                <Form.Item
                  name="for"
                  rules={[{ required: true, message: 'Please enter for' }]}
                >
                  <TextArea disabled={preview} maxLength={255} showCount placeholder='Please enter for (Ex: Retisio eCommerce)'></TextArea>
                </Form.Item>
              </label>
            </Col>
            <Col style={{ flex: 1}}>
              <label style={{ marginRight: 8 }}>
                <strong>Duration</strong>:
                <Form.Item
                  name="duration"
                  rules={[{ required: true, message: 'Please enter duration' }]}
                >
                  <TextArea disabled={preview} maxLength={255} showCount placeholder='Please enter duration (Ex: Aug 25)'></TextArea>
                </Form.Item>
              </label>
            </Col>
          </Row>
        }
        <div style={{ display: 'flex', fontWeight: 'bold', marginBottom: 8, borderBottom: '1px solid #eee' }}>
          <h2>Subscribed items</h2>
        </div>
        <div style={{ display: 'flex', fontWeight: 'bold', marginBottom: 8, borderBottom: '1px solid #eee' }}>
          { (subscriptions.length > 1) &&
            <div style={{ width: 60 }}></div>
          }
          <div style={{ flex: 1 }}>Product Name (SKU)</div>
          <div style={{ flex: 1 }}>Start Date - End Date</div>
          <div style={{ flex: 1 }}>Term</div>
          <div style={{ flex: 1 }}>Description</div>
          <div style={{ flex: invoiceType === 2 ? 2 : 1 }}>
            Original Price
            {
              invoiceType === 2 && (
                '/Unit'
              )
            }
          </div>
          {
            invoiceType === 2 && (
              <div style={{ flex: invoiceType === 2 ? 1.5 : 1 }}>
                Quantity & Unit(s)
              </div>
            )
          }
          <div style={{ flex: 1 }}>Discount (%)</div>
          <div style={{ flex: 1 }}>Discount Amount</div>
          <div style={{ flex: 1 }}>Sale Price</div>
          <div style={{ width: 40 }}></div>
        </div>
        <Form.List name="subscription_items">
          {(fields, { add, remove }) => (
            <>
              <>
                {fields.map(({ key, name, ...restField }, index) => {
                  let isActive = true;
                  if (subscriptions[index] && Object.prototype.hasOwnProperty.call(subscriptions[index], 'is_active')) {
                    isActive = subscriptions[index].is_active;
                  }
                  const rowValue = form.getFieldValue(['subscription_items']);
                  const isCreditMemo = rowValue[index]['is_credit_memo'];
                  const startDate = rowValue[index]['start_date'];
                  const endDate = rowValue[index]['end_date'];
                  let unitPrice = rowValue[index]['unit_price'];
                  const discountPercentage = rowValue[index]['discount_percentage'];
                  const discountAmount = rowValue[index]['discount_amount'];
                  let finalAmount = rowValue[index]['amount'];
                  if (isCreditMemo) {
                    finalAmount = 0 - +finalAmount;
                    unitPrice = 0 - +unitPrice;
                  }
                  const productId = rowValue[index]['product_id'];
                  const isBundle = rowValue[index]['is_bundled'] ? 'Yes' : 'No';
                  const term = rowValue[index]['term'];
                  const invoiceDescription = rowValue[index]['invoice_description'];
                  const quantity = rowValue[index]['quantity'];
                  return (
                    <Row 
                      style={{ 
                        display: 'flex', 
                        marginBottom: 8, 
                        borderBottom: '1px solid #eeeeee', 
                        padding: '8px 0',
                        backgroundColor: index % 2 === 0 ? '#fafafa' : '#ffffff',
                        alignItems: 'flex-start'
                      }} 
                      key={key} 
                      gutter={16} 
                      align="middle"
                    >
                    {(fields.length > 1 && !preview) && (
                      <Col style={{ width: 60 }}>
                          <>
                            <Button
                              onClick={() => {
                                const values = form.getFieldValue('subscription_items');
                                const newValues = [...values];
                                [newValues[index - 1], newValues[index]] = [newValues[index], newValues[index - 1]];
                                form.setFieldsValue({ subscription_items: newValues || [] });
                              }}
                              disabled={index === 0}
                            >
                              <UpCircleOutlined style={{ fontSize: 20 }}/>
                            </Button>
                            <Button
                              onClick={() => {
                                const values = form.getFieldValue('subscription_items');
                                const newValues = [...values];
                                [newValues[index], newValues[index + 1]] = [newValues[index + 1], newValues[index]];
                                form.setFieldsValue({ subscription_items: newValues || [] });
                              }}
                              disabled={index === subscriptions.length - 1}
                            >
                              <DownCircleOutlined style={{ fontSize: 20 }}/>
                            </Button>
                          </>
                        </Col>
                      )}
                      <Col style={{ flex: 1 }}>
                        <Typography.Text>
                          { getProductById(productId) }
                        </Typography.Text>
                        <Form.Item
                          {...restField}
                          name={[name, 'product_id']}
                          noStyle
                        >
                          <input type='hidden'/>
                        </Form.Item>
                      </Col>
                      <Col style={{ flex: 1 }}>
                        <Typography.Text>
                          { formatDate(startDate) } - { formatDate(endDate) }
                        </Typography.Text>
                        <Form.Item
                          {...restField}
                          name={[name, 'start_date']}
                          noStyle
                        >
                          <input type='hidden'/>
                        </Form.Item>
                        <Form.Item
                          {...restField}
                          name={[name, 'end_date']}
                          noStyle
                        >
                          <input type='hidden'/>
                        </Form.Item>
                      </Col>
                      <Col style={{ flex: 1 }}>
                        <Typography.Text>
                          { term }
                        </Typography.Text>
                      </Col>
                      <Col style={{ flex: 1 }}>
                        <Form.Item
                            name={[name, 'invoice_description']}
                            rules={[
                              { required: true, message: 'Please enter description' },
                            ]}
                          >
                            <TextArea disabled={preview} maxLength={255} showCount placeholder='Enter description of the item'></TextArea>
                          </Form.Item>
                      </Col>
                      <Col style={{ flex: invoiceType === 2 ? 2 : 1 }}>
                      {
                          term === 'Billed on actual' ? 
                            <>
                              {
                                invoiceType === 1 ? (
                                  <Form.Item
                                    name={[name, 'unit_price']}
                                    rules={[
                                      { type: 'number', transform: (value) => Number(value), message: 'Enter a valid number' },
                                    ]}
                                  >
                                    <InputNumber style={{width: '80%'}} step={0.01} min={0} placeholder="Enter unit price" />
                                  </Form.Item>
                                ) : (
                                  <Input.Group compact>
                                    <Row gutter={1} style={{ display: 'flex' }}>
                                      <Col span={8}>
                                        <Form.Item
                                          name={[name, 'unit_price']}
                                          rules={[
                                            { type: 'number', transform: (value) => Number(value), message: 'Enter a valid number' },
                                          ]}
                                        >
                                          <InputNumber style={{ width: '100%' }} step={0.01} min={0} placeholder="Enter unit price" />
                                        </Form.Item>
                                      </Col>
                                      <Col span={2}><div style={{ margin: '5px'}}>/</div></Col>
                                      <Col span={8}>
                                        <Form.Item
                                          name={[name, 'price_unit']}
                                          rules={[
                                            { required: true, message: 'Please enter unit' },
                                          ]}
                                        >
                                          <Input placeholder="Unit"/>
                                        </Form.Item>
                                      </Col>
                                    </Row>
                                  </Input.Group>
                                )
                              }
                            </> :
                            <>
                              {
                                invoiceType === 1 ? (
                                  <>
                                    <Typography.Text>
                                      { formatPrice(unitPrice) }
                                    </Typography.Text>
                                    <Form.Item
                                      {...restField}
                                      name={[name, 'unit_price']}
                                    >
                                      <input type='hidden'/>
                                    </Form.Item>
                                  </>
                                ) : (
                                  <Input.Group compact style={{ display: 'flex', alignItems: 'center' }}>
                                    <Row gutter={1} style={{ display: 'flex' }}>
                                      <Col span={!isCreditMemo ? 8: 24}>
                                        <Typography.Text style={{ width: '120%', textAlign: 'center' }}>
                                          { formatPrice(unitPrice) }
                                        </Typography.Text>
                                        <Form.Item
                                          {...restField}
                                          name={[name, 'unit_price']}
                                        >
                                          <input type='hidden'/>
                                        </Form.Item>
                                      </Col>
                                      {
                                        !isCreditMemo && (
                                          <>
                                            <Col span={2}><div style={{ margin: '5px'}}>/</div></Col>
                                            <Col span={8}>
                                              <Form.Item
                                                name={[name, 'price_unit']}
                                                rules={[
                                                  { required: true, message: 'Please enter unit' },
                                                ]}
                                              >
                                                <Input step={0.01} min={0} placeholder="Unit"/>
                                              </Form.Item>
                                            </Col>
                                          </>
                                        )
                                      }
                                    </Row>
                                  </Input.Group>
                                )
                              }
                            </>
                        }
                      </Col>
                      {
                        invoiceType === 2 && (
                          <Col style={{ flex: invoiceType === 2 ? 1.5 : 1 }}>
                            <Input.Group compact>
                              <Row gutter={1} style={{ display: 'flex' }}>
                                {
                                  isCreditMemo ? (
                                    <Col span={12}>
                                        <Typography.Text style={{ width: '100%', textAlign: 'center' }}>
                                          { quantity }
                                        </Typography.Text>
                                        <Form.Item
                                          {...restField}
                                          name={[name, 'quantity']}
                                        >
                                          <input type='hidden'/>
                                        </Form.Item>
                                      </Col>
                                  ) : (
                                    <Col span={!isCreditMemo ? 12 : 24}>
                                      <Form.Item
                                        name={[name, 'quantity']}
                                        rules={[
                                          { type: 'number', transform: (value) => Number(value), message: 'Enter a valid number' },
                                        ]}
                                      >
                                        <InputNumber min={1} placeholder="Enter quantity" />
                                      </Form.Item>
                                    </Col>
                                  )
                                }
                                {
                                  !isCreditMemo && (
                                    <Col span={12}>
                                      <Form.Item
                                        name={[name, 'quantity_unit']}
                                        rules={[
                                          { required: true, message: 'Please enter unit' },
                                        ]}
                                      >
                                        <Input step={0.01} min={0} placeholder="Unit" />
                                      </Form.Item>
                                    </Col>
                                  )
                                }
                              </Row>
                              
                            </Input.Group>
                          </Col>
                        )
                      }
                      <Col style={{ flex: 1 }}>
                      {
                          term === 'Billed on actual' ? 
                            <>
                              <Form.Item
                                name={[name, 'discount_percentage']}
                                rules={[
                                  { type: 'number', transform: (value) => Number(value), message: 'Enter a valid number' },
                                ]}
                              >
                                <InputNumber style={{width: '80%'}} step={0.01} min={0} max={100} placeholder="Enter discount percentage" />
                              </Form.Item>
                            </> :
                            <>
                              <Typography.Text>
                                { `${formatPrice(discountPercentage, false)}%` }
                              </Typography.Text>
                              <Form.Item
                                {...restField}
                                name={[name, 'discount_percentage']}
                              >
                                <input type='hidden'/>
                              </Form.Item>
                            </>
                        }
                      </Col>
                      <Col style={{ flex: 1 }}>
                        <Typography.Text>
                          { formatPrice(discountAmount) }
                        </Typography.Text>
                        <Form.Item
                          {...restField}
                          name={[name, 'discount_amount']}
                          noStyle
                        >
                          <input type='hidden'/>
                        </Form.Item>
                      </Col>
                      <Col style={{ flex: 1 }}>
                        <Typography.Text>
                          { formatPrice(finalAmount) }
                        </Typography.Text>
                        <Form.Item
                          {...restField}
                          name={[name, 'amount']}
                          noStyle
                        >
                          <input type='hidden'/>
                        </Form.Item>
                      </Col>
                      <Col style={{ width: 40 }}>
                        {(fields.length > 1 && !preview) && (
                          <MinusCircleOutlined
                            onClick={() => remove(name)}
                            style={{ color: 'red', fontSize: 20 }}
                          />
                        )}
                      </Col>
                    </Row>
                  )}
                )}
              </>
            </>
          )}
        </Form.List>
        {
          subscriptions.length > 0 && (
            <>
              <div style={{ display: 'flex', fontWeight: 'bold', borderBottom: '1px solid #eee' }}>
                <div style={{ flex: 1 }}></div>
                <div style={{ flex: 1 }}></div>
                <div style={{ flex: 1 }}></div>
                <div style={{ flex: 1 }}></div>
                <div style={{ flex: 1 }}></div>
                <div style={{ flex: 1 }}></div>
                <div style={{ flex: 1 }}>Sub Total (A)</div>
                <div style={{ flex: 1 }}>
                  <Typography.Text>
                    { formatPrice(formValues['sub_total']) }
                  </Typography.Text>
                  <Form.Item
                    name='sub_total'
                    noStyle
                  >
                    <input type='hidden'/>
                  </Form.Item>
                </div>
              </div>
              <div style={{ display: 'flex', fontWeight: 'bold', borderBottom: '1px solid #eee' }}>
                <div style={{ flex: 1 }}></div>
                <div style={{ flex: 1 }}></div>
                <div style={{ flex: 1 }}></div>
                <div style={{ flex: 1 }}></div>
                <div style={{ flex: 1 }}></div>
                <div style={{ flex: 1 }}></div>
                <div style={{ flex: 1 }}>Discount (B)</div>
                <div style={{ flex: 1 }}>
                  <Typography.Text>
                    { formatPrice(formValues['total_discount']) }
                  </Typography.Text>
                  <Form.Item
                    name='total_discount'
                    noStyle
                  >
                    <input type='hidden'/>
                  </Form.Item>
                </div>
              </div>
              <div style={{ display: 'flex', fontWeight: 'bold', borderBottom: '1px solid #eee' }}>
                <div style={{ flex: 1 }}></div>
                <div style={{ flex: 1 }}></div>
                <div style={{ flex: 1 }}></div>
                <div style={{ flex: 1 }}></div>
                <div style={{ flex: 1 }}></div>
                <div style={{ flex: 1 }}></div>
                <div style={{ flex: 1 }}>Payments/Credit (C)</div>
                <div style={{ flex: 1 }}>
                  <Form.Item
                    name='credits'
                    rules={[
                      { type: 'number', transform: (value) => Number(value), message: 'Enter a valid number' },
                    ]}
                  >
                    <InputNumber style={{width: '80%'}} step={0.01} min={0} placeholder="Enter credit" />
                  </Form.Item>
                </div>
              </div>
              <div style={{ display: 'flex', fontWeight: 'bold', borderBottom: '1px solid #eee' }}>
                <div style={{ flex: 1 }}></div>
                <div style={{ flex: 1 }}></div>
                <div style={{ flex: 1 }}></div>
                <div style={{ flex: 1 }}></div>
                <div style={{ flex: 1 }}></div>
                <div style={{ flex: 1 }}></div>
                <div style={{ flex: 1 }}>Total (D) (D = A-B-C)</div>
                <div style={{ flex: 1 }}>
                  <Typography.Text>
                    { formatPrice(formValues['total'] - formValues['tax']) }
                  </Typography.Text>
                </div>
              </div>
              <div style={{ display: 'flex', fontWeight: 'bold', borderBottom: '1px solid #eee' }}>
                <div style={{ flex: 1 }}></div>
                <div style={{ flex: 1 }}></div>
                <div style={{ flex: 1 }}></div>
                <div style={{ flex: 1 }}></div>
                <div style={{ flex: 1 }}></div>
                <div style={{ flex: 1 }}></div>
                <div style={{ flex: 1 }}>Total Tax (E)</div>
                <div style={{ flex: 1 }}>
                  <Typography.Text>
                    { formatPrice(formValues['tax']) }
                  </Typography.Text>
                  <Form.Item
                    name='tax'
                    noStyle
                  >
                    <input type='hidden'/>
                  </Form.Item>
                </div>
              </div>
              <div style={{ display: 'flex', fontWeight: 'bold', borderBottom: '1px solid #eee' }}>
                <div style={{ flex: 1 }}></div>
                <div style={{ flex: 1 }}></div>
                <div style={{ flex: 1 }}></div>
                <div style={{ flex: 1 }}></div>
                <div style={{ flex: 1 }}></div>
                <div style={{ flex: 1 }}></div>
                <div style={{ flex: 1 }}>Balance Due (D+E)</div>
                <div style={{ flex: 1 }}>
                  <Typography.Text>
                    { formatPrice(formValues['total']) }
                  </Typography.Text>
                  <Form.Item
                    name='total'
                    noStyle
                  >
                    <input type='hidden'/>
                  </Form.Item>
                </div>
              </div>
            </>
          )
        }
        <Form.Item>
          <>
          {!preview && <Button loading={loading} htmlType="submit" type="primary" style={{marginRight: "10px"}} disabled={!subscriptions.length || preview}>Generate Invoice</Button>}
          {
            preview && (
              <>
                {
                  isCommitted === 0 && (
                    <>
                      <Button loading={loading} onClick={() => submitInvoice(1)} style={{marginRight: "10px"}} disabled={!preview}>Commit</Button>
                      <Button loading={loading} onClick={() => submitInvoice(2)} style={{marginRight: "10px"}} disabled={!preview}>Void</Button>
                    </>
                  )
                }
                <Button onClick={() => submitInvoice(0)} disabled={!preview}>Download PDF</Button>
              </>
            )
          }
          </>
        </Form.Item>
      </Form>

      <Collapse className='mt-24'>
        {
          preview && preview?.summary?.length > 0 && (
            <Collapse.Panel header="Tax Breakup Details" key="2">
              <Table columns={tax_columns} dataSource={preview.summary} rowKey={"jurisCode"} pagination={false}></Table>
            </Collapse.Panel>
          )
        }
        {
          isBundled && bundledProducts.length > 0 && (
            <Collapse.Panel header="Bundled Products" key="1">
              <Table columns={bundle_rows} dataSource={bundledProducts} rowKey={'id'} pagination={false}></Table>
            </Collapse.Panel>
          )
        }
      </Collapse>
    </>
  );
}

export default GenerateInvoice;
